/**
 * Country Service
 * Handles country data operations
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError, of, shareReplay } from 'rxjs';
import { APP_SCHEMA_ENDPOINTS } from '../api';

export interface CountryRead {
  country_id: number;
  code: string;
  country_name: string;
}

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  private readonly http = inject(HttpClient);
  private countriesLoaded = false;

  // Signals for reactive state management
  readonly countries = signal<CountryRead[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Get all countries
   * ✅ With caching to prevent duplicate API calls
   */
  getCountries(): Observable<CountryRead[]> {
    // ✅ Return cached data if already loaded
    if (this.countriesLoaded && this.countries().length > 0) {
      return of(this.countries());
    }

    // ✅ Prevent parallel requests if already loading
    if (this.loading()) {
      console.log('✅ Countries: Already loading, returning cached');
      return of(this.countries());
    }

    this.loading.set(true);
    this.error.set(null);

    return this.http
      .get<CountryRead[]>(`${APP_SCHEMA_ENDPOINTS.countries.list}?limit=300`, {
        headers: this.createHeaders(),
      })
      .pipe(
        tap((countries) => {
          this.countries.set(countries);
          this.countriesLoaded = true;
          this.loading.set(false);
        }),
        shareReplay(1),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Get country name by ID
   */
  getCountryNameById(countryId: number | null | undefined): string {
    if (!countryId) return '-';
    const country = this.countries().find((c) => c.country_id === countryId);
    return country?.country_name || '-';
  }

  /**
   * Create HTTP headers
   */
  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  /**
   * Handle errors
   */
  private handleError(error: unknown): Observable<never> {
    this.loading.set(false);
    const errorMessage = error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten';
    this.error.set(errorMessage);
    console.error('❌ CountryService Error:', error);
    return throwError(() => error);
  }
}
